
import { Vue, Component, Watch } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
const namespace: string = 'cart';

@Component<CartPayWhatYouCan>({
	components: {
		VueSlider
	}
})
export default class CartPayWhatYouCan extends Vue {
	@Action('setChosenSubsidyDiscount', { namespace }) private setChosenSubsidyDiscount!: (chosenAmountToBePaid: number) => void;
	@Getter('getSubtotal', { namespace }) private subtotal!: string;
	@Getter('getItems', { namespace }) private cartItems!: OrderItem[];
	@Getter('getRotationalMenuOrders', { namespace }) private rotationalMenuOrders!: RotationalMenuOrder[];
	@Getter('getSubsidyChosenAmountToBePaid', { namespace }) private subsidyChosenAmountToBePaid!: string | undefined;
	@Getter('getSubsidyDiscountAmount', { namespace }) private subsidyDiscountAmount!: string;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Watch('maxValue', { immediate: true })
	private onMaxValueChanged(newValue: number, oldValue?: number): void {
		if(newValue <= 0) {
			return;
		}

		// If this is the first time (initial setup)
		if (!this.initialValueSet) {
			// Check if there's already a subsidy amount set
			if (typeof this.subsidyChosenAmountToBePaid === 'string') {
				this.sliderValue = +this.subsidyChosenAmountToBePaid;
				this.setChosenSubsidyDiscount(this.sliderValue);
			}
			// Set to max value if no subsidy amount is set
			else {
				this.sliderValue = newValue;
				this.setChosenSubsidyDiscount(this.sliderValue);
				this.$emit('slider-value-updated', this.sliderValue);
			}
			this.initialValueSet = true;
		}
		// If max value decreased and current slider value exceeds the new max
		else if (oldValue !== undefined && newValue < oldValue && this.sliderValue > newValue) {
			this.sliderValue = newValue;
			this.setChosenSubsidyDiscount(this.sliderValue);
			this.$emit('slider-value-updated', this.sliderValue);
		}
		// If current slider value is below zero, adjust it to zero
		else if (this.sliderValue < 0) {
			this.sliderValue = 0;
			this.setChosenSubsidyDiscount(this.sliderValue);
			this.$emit('slider-value-updated', this.sliderValue);
		}
	}

	private minValue: number = 0;
	private sliderValue: number = 0;
	private editValue: string = '0.00';
	private isEditing: boolean = false;
	private initialValueSet: boolean = false;
	private preventNextSliderChangeEvent: boolean = false;

	private get payWhatYouCanMaxAmount(): string {
		if (!this.restaurant || !this.restaurant.subsidies) {
			return (+this.subtotal).toFixed(2);
		}

		const activeSubsidy = this.restaurant.subsidies.find(subsidy =>
			subsidy.is_active &&
			subsidy.type === 'pay_what_you_can'
		);

		// If subsidy targets meals only, max = sum of meal items only
		if (activeSubsidy?.target_items === 'meals') {
			let mealsOnlySubtotal = 0;

			this.allCartItems.forEach(item => {
				if (item.meal_package && item.orderPrice) {
					mealsOnlySubtotal += item.orderPrice;
				}
			});
			return mealsOnlySubtotal.toFixed(2);
		}

		return (+this.subtotal).toFixed(2);
	}

	/**
	 * Get all cart items, including items from rotational menu orders for K12 locations.
	 */
	private get allCartItems(): OrderItem[] {
		if (this.restaurant?.k12 && this.rotationalMenuOrders?.length) {
			return this.rotationalMenuOrders.flatMap(order => order.items);
		}
		return this.cartItems;
	}

	private get maxValue(): number {
		return +this.payWhatYouCanMaxAmount;
	}

	private get selectedValue(): string {
		return this.sliderValue.toFixed(2);
	}

	private get subsidyMessage(): string {
		// No subsidizable items (maxValue is 0 or less)
		if (this.maxValue <= 0) {
			return this.$t('checkout.cart.pricing.pay_what_you_can.no_subsidizable_items') as string;
		}

		// Check if this is a meals-only subsidy with non-meal items in cart
		const activeSubsidy = this.restaurant?.subsidies?.find(subsidy =>
			subsidy.is_active &&
			subsidy.type === 'pay_what_you_can'
		);

		if (activeSubsidy?.target_items === 'meals') {
			const hasNonMealItems = this.allCartItems.some(item => !item.meal_package && item.orderPrice);
			if (hasNonMealItems) {
				return this.$t('checkout.cart.pricing.pay_what_you_can.meals_only_message') as string;
			}
		}

		// No message needed
		return '';
	}


	private onSliderDrag(value: number): void {
		this.sliderValue = value;
	}

	/**
	 * Handle the slider change event.
	 * Prevents the next change if `preventNextSliderChangeEvent` is true. This is to avoid a
	 * weird problem where the slider thinks the value has changed when it hasn't. But we still
	 * want the slider to listen to drag events even if the user is editing, thus we need to use this
	 * one time flag.
	 *
	 * @return {void}
	 */
	private onSliderChange(): void {
		if (this.preventNextSliderChangeEvent) {
			this.preventNextSliderChangeEvent = false;
			return;
		}
		this.setChosenSubsidyDiscount(this.sliderValue);
		this.$emit('slider-value-updated', this.sliderValue);
	}

	/**
	 * Starts editing the slider value by showing an input field.
	 *
	 * @return {void}
	 */
	private startEditing(): void {
		this.preventNextSliderChangeEvent = true;
		this.isEditing = true;
		this.editValue = this.sliderValue.toFixed(2);

		// Focus input
		this.$nextTick(() => {
			const input = this.$refs.tooltipInput as HTMLInputElement;
			if (input) {
				input.focus();
				input.select();
			}
		});
	}

	/**
	 * Saves the edited value from the input field,
	 * clamping it to min or max if out of range respectively.
	 *
	 * @return {void}
	 */
	private saveEdit(): void {
		const value = +this.editValue;
		this.preventNextSliderChangeEvent = false;

		if (isNaN(value)) {
			this.isEditing = false;
			return;
		}

		this.sliderValue = Math.min(Math.max(value, this.minValue), this.maxValue);
		this.onSliderChange();
		this.isEditing = false;
	}

	private cancelEdit(): void {
		this.preventNextSliderChangeEvent = false;
		this.isEditing = false;
		this.editValue = this.sliderValue.toFixed(2);
	}
}
